import React, { useState, useEffect } from 'react';
import { couponService } from '../../services/couponService';
import BaseModal from '../common/BaseModal';

function SimpleHistoryModal({ couponId, isOpen, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && couponId) {
      fetchCouponHistory();
    } else if (!isOpen) {
      setHistory(null);
      setError('');
    }
  }, [isOpen, couponId]);

  const fetchCouponHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await couponService.getCouponHistory(couponId);
      setHistory(response.data);
    } catch (err) {
      setError('Blad podczas pobierania historii kuponu');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pl-PL');
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '-';
    const n = parseFloat(amount);
    if (Number.isNaN(n)) return '-';
    return `${n.toFixed(2)} zł`;
  };

  // Szczegoly zdarzen przychodza z backendu z technicznymi kluczami (receipt_number,
  // cashier...). Kasjer ma czytac po polsku - klucz nieznany pokazujemy jak jest.
  const ETYKIETY = {
    value: 'Wartość początkowa',
    initial_value: 'Wartość początkowa',
    balance: 'Saldo po zdarzeniu',
    expiry_date: 'Ważna do',
    shop: 'Sklep',
    payment_method: 'Płatność za kartę',
    amount_used: 'Zdjęto z karty',
    receipt_number: 'Paragon',
    transaction_id: 'Nr transakcji',
    location_id: 'Sklep (id)',
    cashier: 'Kasjer',
    note: 'Uwagi',
  };
  const KWOTY = new Set(['value', 'initial_value', 'balance', 'amount_used']);
  const POMIN = new Set(['transaction_id', 'location_id']);
  const formatDetail = (key, value) => (KWOTY.has(key) ? formatCurrency(value) : (value === null || value === undefined || value === '' ? '-' : String(value)));

  const footerContent = (
    <button
      onClick={onClose}
      style={{
        padding: '6px 12px',
        background: 'var(--bg)',
        color: 'var(--text)',
        border: '1px solid var(--border)',
        fontWeight: 500,
        fontSize: '12px',
      }}
    >
      Zamknij
    </button>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Historia kuponu"
      size="md"
      zIndex={1000}
      headerStyle={{
        border: '1px solid var(--info)',
        color: 'var(--info)',
        padding: '22px 28px 18px 28px',
      }}
      bodyStyle={{ padding: '28px' }}
      footer={footerContent}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div>Ladowanie...</div>
        </div>
      )}
      {error && (
        <div style={{ color: 'var(--danger)', background: 'var(--danger-light)', borderRadius: 5, padding: '8px 12px', marginBottom: 14, fontSize: 15 }}>{error}</div>
      )}
      {history && (
        <div>
          <div style={{ marginBottom: '20px', padding: '15px', border: '1.5px solid var(--border)', borderRadius: 7, background: 'var(--bg)' }}>
            <h4 style={{ margin: 0, fontWeight: 600, fontSize: 17, color: 'var(--text)' }}>Informacje o kuponie</h4>
            <div style={{ fontSize: 15, marginTop: 8 }}>
              <div><strong>Kod:</strong> {history.coupon.kod}</div>
              <div><strong>Saldo:</strong> {formatCurrency(history.coupon.wartosc)}</div>
              {history.coupon.wartosc_poczatkowa !== undefined && (
                <div><strong>Wartość początkowa:</strong> {formatCurrency(history.coupon.wartosc_poczatkowa)}</div>
              )}
              <div><strong>Status:</strong> {history.coupon.status}{history.status_info?.is_expired && history.coupon.status === 'aktywny' ? ' (po terminie - kasa nie przyjmie)' : ''}</div>
              <div><strong>Data waznosci:</strong> {history.coupon.data_waznosci}</div>
              {history.coupon.numer_telefonu && (
                <div><strong>Telefon:</strong> {history.coupon.numer_telefonu}</div>
              )}
              {history.coupon.numer_paragonu && (
                <div><strong>Paragon:</strong> {history.coupon.numer_paragonu}</div>
              )}
            </div>
          </div>
          <div>
            <h4 style={{ margin: '18px 0 10px 0', fontWeight: 600, fontSize: 17, color: 'var(--text)' }}>Historia wydarzen</h4>
            {history.history && history.history.length > 0 ? (
              history.history.map((event, index) => (
                <div key={index} style={{
                  padding: '12px 14px',
                  marginBottom: '12px',
                  border: '1.5px solid var(--border)',
                  borderRadius: 7,
                  background: 'var(--bg)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{event.description}</div>
                  <div style={{ fontSize: '0.97em', color: 'var(--text-secondary)', marginTop: 2 }}>
                    {formatDate(event.date)}
                  </div>
                  {event.details && (
                    <div style={{ fontSize: '0.97em', marginTop: '7px' }}>
                      {Object.entries(event.details).filter(([key]) => !POMIN.has(key)).map(([key, value]) => (
                        <div key={key}>
                          <strong>{ETYKIETY[key] || key}:</strong> {formatDetail(key, value)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: 15, textAlign: 'center', margin: '18px 0' }}>Brak historii dla tego kuponu</div>
            )}
          </div>
        </div>
      )}
    </BaseModal>
  );
}

export default SimpleHistoryModal;
